import type { FunctionComponent } from 'react';
import type { z } from 'zod';
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import type { LaunderingSignalItemSchema } from '@ballerine/business-analysis-schemas';
import type { MerchantReportRiskLevel } from '@ballerine/common';

import { severityToClassName } from '@/common/constants';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';
import { ReportMarkdown } from '@/components/templates/report-shared/components/ReportMarkdown';

type Signal = z.infer<typeof LaunderingSignalItemSchema>;

type KeyRiskSignalsProps = {
  signals: Signal[];
};

const severityLabels: Record<MerchantReportRiskLevel, string> = {
  critical: 'CRITICAL RISK',
  high: 'HIGH RISK',
  medium: 'MEDIUM RISK',
  low: 'LOW RISK',
};

const WarningIcon: FunctionComponent<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2L1 21h22L12 2z" />
    <path d="M12 15V9" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="18" r="1" fill="white" />
  </svg>
);

const SeverityBadge: FunctionComponent<{ severity: string }> = ({ severity }) => {
  const normalizedSeverity = severity.toLowerCase() as MerchantReportRiskLevel;
  const className = severityToClassName[normalizedSeverity] ?? severityToClassName.medium;
  const label = severityLabels[normalizedSeverity] ?? 'UNKNOWN';

  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${className}`}>
      {label}
    </span>
  );
};

const SignalCard: FunctionComponent<{ signal: Signal }> = ({ signal }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <CollapsibleTrigger className="flex w-full items-center gap-3 p-4 text-left">
          <WarningIcon className="h-5 w-5 flex-shrink-0 text-yellow-500" />
          <span className="flex-1 text-sm font-semibold text-gray-900">{signal.signal}</span>
          <SeverityBadge severity={signal.severity} />
          <ChevronRight
            className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-90' : ''
            }`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
          <div className="border-t border-gray-100 px-4 pb-4 pt-3">
            <ReportMarkdown>{signal.summary}</ReportMarkdown>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const KeyRiskSignals: FunctionComponent<KeyRiskSignalsProps> = ({ signals }) => {
  if (!signals || signals.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
      <div className="mb-4">
        <h2 className="text-xl font-semibold" style={{ color: '#14203D' }}>
          Key Risk Signals
        </h2>
      </div>
      <div className="space-y-3">
        {signals.map(signalObj => (
          <SignalCard key={signalObj.signal} signal={signalObj} />
        ))}
      </div>
    </div>
  );
};
